import {
	ApiError,
	type Lot,
	type LotExposure,
	type LowStockState,
	type PortalReceivingRow,
	type PortalReorderDismissReason,
	type PortalReorderRow,
	type PortalStockRow,
	type ReceivingStatus,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { formatDate } from "@khazzin/i18n/dates";
import { EmptyState, KpiTile, PageHeader } from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import {
	Button,
	Dialog,
	Field,
	Input,
	LinkTabs,
	NumberInput,
	Select,
} from "@khazzin/ui/primitives";
import type { Tone } from "@khazzin/ui/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { useApi } from "../../api";
import { SearchInput } from "../../app/SearchInput";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";

/**
 * The Store Portal's Inventory (EP10-S06, DA-10-06): Stock, Low Stock,
 * Receiving, Lots (ADJ-R01-12) and What to reorder, one route each.
 *
 * **Every number is the server's.** Available, the alert, the suggested
 * quantity: EP-02 computes them and this screen draws them.
 *
 * **The store is the query key's**, after `portal`, and `inventory` after it:
 * the shell's live stream invalidates `["portal", storeId, "inventory"]` on an
 * `inventory.stock_changed` hint, and re-reads every 60 s while the stream is
 * down (EP10-S22).
 *
 * Accept, Dismiss and the target days are the write level's; at `read` they
 * are not drawn.
 */

export const INVENTORY_TABS = ["stock", "low-stock", "receiving", "lots", "reorder"] as const;
export type InventoryTab = (typeof INVENTORY_TABS)[number];

/** The tab a route names; anything else is Stock. */
export function inventoryTabOf(raw: string | undefined): InventoryTab {
	return INVENTORY_TABS.find((tab) => tab === raw) ?? "stock";
}

export function portalInventoryKey(storeId: string) {
	return ["portal", storeId, "inventory"] as const;
}

const ALERT_TONE: Record<LowStockState, Tone> = {
	ok: "success",
	low: "warning",
	critical: "danger",
};

const RECEIVING_TONE: Record<ReceivingStatus, Tone> = {
	announced: "info",
	arrived: "info",
	receiving: "warning",
	received: "success",
	closed: "neutral",
	cancelled: "danger",
};

export const DISMISS_REASONS: readonly PortalReorderDismissReason[] = [
	"already_ordered",
	"discontinued",
	"seasonal",
	"enough_stock",
	"other",
];

/** The smallest target days on hand (`02/08` §3); the server refuses less. */
export const MIN_TARGET_DAYS = 7;

function count(n: number | null | undefined): string {
	return n === null || n === undefined ? "—" : String(n);
}

function decimal(n: number | null | undefined, locale: string): string {
	return n === null || n === undefined
		? "—"
		: new Intl.NumberFormat(`${locale}-u-nu-latn`, { maximumFractionDigits: 2 }).format(n);
}

function AlertChip({ alert, t }: { alert: LowStockState; t: Translate }) {
	return <StatusChip tone={ALERT_TONE[alert]}>{t(`portal.inventory.alert.${alert}`)}</StatusChip>;
}

export function InventoryPage() {
	const { t } = useLocale();
	const { store } = useStoreScope();
	const params = useParams();
	const level = levelOf(store, "inventory");
	const storeId = store?.storeId ?? "";
	const tab = inventoryTabOf(params.tab);

	if (level === "none" || (params.tab !== undefined && params.tab !== tab)) {
		return <NotFound />;
	}

	return (
		<div className="flex flex-col gap-4">
			<PageHeader title={t("portal.inventory.title")} subtitle={t("portal.inventory.subtitle")} />
			<LinkTabs
				aria-label={t("portal.inventory.tabs.label")}
				tabs={INVENTORY_TABS.map((value) => ({
					to: value === "stock" ? "/inventory" : `/inventory/${value}`,
					label: t(`portal.inventory.tabs.${value}`),
					end: true,
				}))}
			/>
			{tab === "stock" && <StockTab storeId={storeId} />}
			{tab === "low-stock" && <LowStockTab storeId={storeId} />}
			{tab === "receiving" && <ReceivingTab storeId={storeId} />}
			{tab === "lots" && <LotsTab storeId={storeId} />}
			{tab === "reorder" && <ReorderTab storeId={storeId} writable={level === "write"} />}
		</div>
	);
}

/** SKU, Available and the alert first: the phone's card shows them on top. */
function stockColumns(t: Translate): Column<PortalStockRow>[] {
	return [
		{
			id: "image",
			headerKey: "portal.inventory.columns.image",
			width: 56,
			// EP10-S07: the sku's first photo, signed for five minutes.
			cell: (r) =>
				r.imageUrl ? (
					<img
						src={r.imageUrl}
						alt={r.itemName}
						className="size-8 rounded-sm border border-border object-cover"
					/>
				) : (
					<span aria-hidden className="block size-8 rounded-sm border border-border bg-bg-subtle" />
				),
		},
		{ id: "sku", headerKey: "portal.inventory.columns.sku", mono: true, cell: (r) => r.skuCode },
		{
			id: "available",
			headerKey: "portal.inventory.columns.available",
			align: "end",
			cell: (r) => count(r.available),
		},
		{
			id: "alert",
			headerKey: "portal.inventory.columns.alert",
			cell: (r) => <AlertChip alert={r.alert} t={t} />,
		},
		{
			id: "item",
			headerKey: "portal.inventory.columns.item",
			card: "wide",
			cell: (r) => r.itemName,
		},
		{
			id: "onHand",
			headerKey: "portal.inventory.columns.onHand",
			align: "end",
			cell: (r) => count(r.onHand),
		},
		{
			id: "reserved",
			headerKey: "portal.inventory.columns.reserved",
			align: "end",
			cell: (r) => count(r.reserved),
		},
	];
}

function StockTab({ storeId }: { storeId: string }) {
	const { t } = useLocale();
	const api = useApi();
	const [q, setQ] = useState("");
	const page = useCursorPagination<PortalStockRow>(
		(cursor, limit, signal) =>
			api
				.listPortalStock({ q, limit, signal, ...(cursor === null ? {} : { cursor }) })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...portalInventoryKey(storeId), "stock", q], enabled: storeId !== "" },
	);
	return (
		<div className="flex flex-col gap-3">
			<SearchInput
				value={q}
				onCommit={(next) => {
					setQ(next);
					page.reset();
				}}
				aria-label={t("portal.inventory.search.label")}
				placeholder={t("portal.inventory.search.placeholder")}
				className="max-w-72"
			/>
			<DataTable<PortalStockRow>
				screenKey="portal.inventory.stock"
				caption={t("portal.inventory.tabs.stock")}
				t={t}
				columns={stockColumns(t)}
				rowKey={(r) => r.skuId}
				page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
				status={page.status}
				error={page.error}
				onRetry={page.refetch}
				collapse="cards"
				pagination={{
					showingFrom: page.showingFrom,
					showingTo: page.showingTo,
					hasNext: page.hasNext,
					hasPrevious: page.hasPrevious,
					onNext: page.next,
					onPrevious: page.previous,
				}}
				empty={
					<EmptyState
						title={t("portal.inventory.empty.stockTitle")}
						body={t("portal.inventory.empty.stockBody")}
					/>
				}
			/>
		</div>
	);
}

function LowStockTab({ storeId }: { storeId: string }) {
	const { t } = useLocale();
	const api = useApi();
	const page = useCursorPagination<PortalStockRow>(
		(cursor, limit, signal) =>
			api
				.listPortalLowStock({ limit, signal, ...(cursor === null ? {} : { cursor }) })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...portalInventoryKey(storeId), "low"], enabled: storeId !== "" },
	);
	return (
		<DataTable<PortalStockRow>
			screenKey="portal.inventory.low"
			caption={t("portal.inventory.tabs.low-stock")}
			t={t}
			columns={stockColumns(t)}
			rowKey={(r) => r.skuId}
			page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
			status={page.status}
			error={page.error}
			onRetry={page.refetch}
			collapse="cards"
			pagination={{
				showingFrom: page.showingFrom,
				showingTo: page.showingTo,
				hasNext: page.hasNext,
				hasPrevious: page.hasPrevious,
				onNext: page.next,
				onPrevious: page.previous,
			}}
			empty={
				<EmptyState
					title={t("portal.inventory.empty.lowTitle")}
					body={t("portal.inventory.empty.lowBody")}
				/>
			}
		/>
	);
}

function ReceivingTab({ storeId }: { storeId: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const page = useCursorPagination<PortalReceivingRow>(
		(cursor, limit, signal) =>
			api
				.listPortalReceiving({ limit, signal, ...(cursor === null ? {} : { cursor }) })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...portalInventoryKey(storeId), "receiving"], enabled: storeId !== "" },
	);
	// The expected day is a date the store chose, stored at midnight UTC.
	const day = (at: string | null | undefined) =>
		at ? formatDate(at, { locale, timeZone: "UTC" }) : "—";
	const columns: Column<PortalReceivingRow>[] = [
		{
			id: "reference",
			headerKey: "portal.inventory.columns.reference",
			card: "wide",
			cell: (r) => r.reference || r.asnId,
		},
		{
			id: "status",
			headerKey: "portal.inventory.columns.status",
			cell: (r) => (
				<StatusChip tone={RECEIVING_TONE[r.status]}>
					{t(`portal.inventory.receiving.${r.status}`)}
				</StatusChip>
			),
		},
		{
			id: "expectedAt",
			headerKey: "portal.inventory.columns.expectedAt",
			cell: (r) => day(r.expectedAt),
		},
		{
			id: "received",
			headerKey: "portal.inventory.columns.received",
			align: "end",
			cell: (r) =>
				t("portal.inventory.receiving.counts", {
					received: r.receivedUnits,
					expected: r.expectedUnits,
				}),
		},
	];
	return (
		<DataTable<PortalReceivingRow>
			screenKey="portal.inventory.receiving"
			caption={t("portal.inventory.tabs.receiving")}
			t={t}
			columns={columns}
			rowKey={(r) => r.asnId}
			page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
			status={page.status}
			error={page.error}
			onRetry={page.refetch}
			collapse="cards"
			pagination={{
				showingFrom: page.showingFrom,
				showingTo: page.showingTo,
				hasNext: page.hasNext,
				hasPrevious: page.hasPrevious,
				onNext: page.next,
				onPrevious: page.previous,
			}}
			empty={
				<EmptyState
					title={t("portal.inventory.empty.receivingTitle")}
					body={t("portal.inventory.empty.receivingBody")}
				/>
			}
		/>
	);
}

const EXPOSURE_TONE: Record<LotExposure, Tone> = {
	ok: "success",
	alert: "warning",
	msl_excluded: "warning",
	on_hold: "neutral",
	expired: "danger",
};

/**
 * Lots (ADJ-R01-12, EP02-S29's portal half): the store's batches of tracked
 * skus, earliest expiry first, with where each stands today and what of it is
 * here. Read only — a hold, a release or a disposal is the warehouse's.
 */
function LotsTab({ storeId }: { storeId: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const page = useCursorPagination<Lot>(
		(cursor, limit, signal) =>
			api
				.listPortalLots({ limit, signal, ...(cursor === null ? {} : { cursor }) })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...portalInventoryKey(storeId), "lots"], enabled: storeId !== "" },
	);
	const columns: Column<Lot>[] = [
		{
			id: "lot",
			headerKey: "portal.inventory.columns.lot",
			card: "wide",
			mono: true,
			cell: (r) => r.lotCode,
		},
		{
			id: "sku",
			headerKey: "portal.inventory.columns.sku",
			mono: true,
			cell: (r) => r.skuCode ?? r.skuId,
		},
		{
			id: "expiry",
			headerKey: "portal.inventory.columns.expiry",
			cell: (r) =>
				r.expiryDate === null || r.expiryDate === undefined
					? "—"
					: // A calendar day the supplier printed, not an instant.
						formatDate(r.expiryDate, { locale, timeZone: "UTC" }),
		},
		{
			id: "exposure",
			headerKey: "portal.inventory.columns.exposure",
			cell: (r) => (
				<StatusChip tone={EXPOSURE_TONE[r.exposure]}>
					{t(`portal.inventory.lots.exposure.${r.exposure}`)}
				</StatusChip>
			),
		},
		{
			id: "onHand",
			headerKey: "portal.inventory.columns.onHand",
			align: "end",
			cell: (r) => count(r.balance.onHand),
		},
		{
			id: "reserved",
			headerKey: "portal.inventory.columns.reserved",
			align: "end",
			cell: (r) => count(r.balance.reserved),
		},
	];
	return (
		<DataTable<Lot>
			screenKey="portal.inventory.lots"
			caption={t("portal.inventory.tabs.lots")}
			t={t}
			columns={columns}
			rowKey={(r) => r.lotId}
			page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
			status={page.status}
			error={page.error}
			onRetry={page.refetch}
			collapse="cards"
			pagination={{
				showingFrom: page.showingFrom,
				showingTo: page.showingTo,
				hasNext: page.hasNext,
				hasPrevious: page.hasPrevious,
				onNext: page.next,
				onPrevious: page.previous,
			}}
			empty={
				<EmptyState
					title={t("portal.inventory.empty.lotsTitle")}
					body={t("portal.inventory.empty.lotsBody")}
				/>
			}
		/>
	);
}

/** The server's message for a refused write, or a generic one. */
function refusal(error: unknown, t: Translate): string {
	return error instanceof ApiError ? error.message : t("portal.inventory.reorder.failed");
}

function ReorderTab({ storeId, writable }: { storeId: string; writable: boolean }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const key = [...portalInventoryKey(storeId), "reorder"] as const;
	const query = useQuery({
		queryKey: key,
		queryFn: ({ signal }) => api.listPortalReorderSuggestions({ signal }),
		enabled: storeId !== "",
	});
	const [accepting, setAccepting] = useState<PortalReorderRow | null>(null);
	const [dismissing, setDismissing] = useState<PortalReorderRow | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	// Everything under inventory: an accept's ASN shows in Receiving too.
	const refresh = () =>
		void queryClient.invalidateQueries({ queryKey: portalInventoryKey(storeId) });
	const data = query.data;

	const columns: Column<PortalReorderRow>[] = [
		{ id: "sku", headerKey: "portal.inventory.columns.sku", mono: true, cell: (r) => r.skuCode },
		{
			id: "item",
			headerKey: "portal.inventory.columns.item",
			card: "wide",
			cell: (r) => r.itemName,
		},
		{
			id: "avg",
			headerKey: "portal.inventory.columns.avgDaily",
			align: "end",
			cell: (r) => decimal(r.avgDailySales, locale),
		},
		{
			id: "days",
			headerKey: "portal.inventory.columns.daysOnHand",
			align: "end",
			cell: (r) => decimal(r.daysOnHand, locale),
		},
		{
			id: "suggested",
			headerKey: "portal.inventory.columns.suggested",
			align: "end",
			cell: (r) => r.suggestedQty,
		},
		...(writable
			? [
					{
						id: "actions",
						headerKey: "portal.inventory.columns.actions",
						cell: (r: PortalReorderRow) => (
							<div className="flex flex-wrap gap-1">
								<Button size="sm" variant="secondary" onClick={() => setAccepting(r)}>
									{t("portal.inventory.reorder.accept")}
								</Button>
								<Button size="sm" variant="ghost" onClick={() => setDismissing(r)}>
									{t("portal.inventory.reorder.dismiss")}
								</Button>
							</div>
						),
					},
				]
			: []),
	];

	return (
		<div className="flex flex-col gap-3">
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
				<KpiTile
					t={t}
					label={t("portal.inventory.reorder.below")}
					value={data?.skusBelowTarget ?? "—"}
					status={query.isError ? "error" : data ? "ready" : "loading"}
					onRetry={() => void query.refetch()}
				/>
				<KpiTile
					t={t}
					label={t("portal.inventory.reorder.units")}
					value={data?.unitsSuggested ?? "—"}
					status={query.isError ? "error" : data ? "ready" : "loading"}
					onRetry={() => void query.refetch()}
				/>
				{writable ? (
					data && (
						<TargetDays
							key={data.targetDays}
							current={data.targetDays}
							onSaved={(days) => {
								setNotice(t("portal.inventory.reorder.targetSaved", { days }));
								refresh();
							}}
						/>
					)
				) : (
					<KpiTile
						t={t}
						label={t("portal.inventory.reorder.target")}
						value={data?.targetDays ?? "—"}
						status={query.isError ? "error" : data ? "ready" : "loading"}
					/>
				)}
			</div>
			{notice !== null && (
				<p role="status" className="text-[12.5px] text-fg">
					{notice}
				</p>
			)}
			<DataTable<PortalReorderRow>
				screenKey="portal.inventory.reorder"
				caption={t("portal.inventory.tabs.reorder")}
				t={t}
				columns={columns}
				rowKey={(r) => r.id}
				page={{ items: data?.items ?? [], nextCursor: null }}
				status={query.isError ? "error" : data ? "success" : "loading"}
				error={query.error}
				onRetry={() => void query.refetch()}
				collapse="cards"
				// One read: EP-02 answers the whole list, most urgent first.
				pagination={{
					showingFrom: data && data.items.length > 0 ? 1 : 0,
					showingTo: data?.items.length ?? 0,
					total: data?.items.length,
					hasNext: false,
					hasPrevious: false,
					onNext: () => {},
					onPrevious: () => {},
				}}
				empty={
					<EmptyState
						title={t("portal.inventory.empty.reorderTitle")}
						body={t("portal.inventory.empty.reorderBody")}
					/>
				}
			/>
			{accepting !== null && (
				<AcceptDialog
					row={accepting}
					onClose={() => setAccepting(null)}
					onDone={(message) => {
						setAccepting(null);
						setNotice(message);
						refresh();
					}}
				/>
			)}
			{dismissing !== null && (
				<DismissDialog
					row={dismissing}
					onClose={() => setDismissing(null)}
					onDone={(message) => {
						setDismissing(null);
						setNotice(message);
						refresh();
					}}
				/>
			)}
		</div>
	);
}

/** The target days input, saved on its own button; under 7 is refused inline. */
function TargetDays({ current, onSaved }: { current: number; onSaved: (days: number) => void }) {
	const { t } = useLocale();
	const api = useApi();
	const [value, setValue] = useState(String(current));
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	async function save() {
		const days = Number(value);
		if (!Number.isInteger(days) || days < MIN_TARGET_DAYS) {
			setError(t("portal.inventory.reorder.targetMin", { min: MIN_TARGET_DAYS }));
			return;
		}
		setSaving(true);
		try {
			const saved = await api.putPortalReorderTargetDays(days);
			setError(null);
			onSaved(saved.targetDays);
		} catch (err) {
			setError(
				err instanceof ApiError && err.code === "target_days_too_low"
					? t("portal.inventory.reorder.targetMin", { min: MIN_TARGET_DAYS })
					: refusal(err, t),
			);
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className="flex items-end gap-2 card p-4">
			<Field
				label={t("portal.inventory.reorder.target")}
				error={error ?? undefined}
				className="flex-1"
			>
				{(control) => (
					<NumberInput
						{...control}
						integerOnly
						unit={t("portal.inventory.reorder.daysUnit")}
						value={value}
						onChange={(event) => setValue(event.target.value)}
					/>
				)}
			</Field>
			<Button variant="secondary" disabled={saving} onClick={() => void save()}>
				{t("ui.actions.save")}
			</Button>
		</div>
	);
}

function AcceptDialog({
	row,
	onClose,
	onDone,
}: {
	row: PortalReorderRow;
	onClose: () => void;
	onDone: (message: string) => void;
}) {
	const { t } = useLocale();
	const api = useApi();
	const [date, setDate] = useState("");
	const [qty, setQty] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function submit() {
		if (date === "") {
			setError(t("portal.inventory.reorder.dateRequired"));
			return;
		}
		const override = qty.trim() === "" ? undefined : Number(qty);
		if (override !== undefined && (!Number.isInteger(override) || override < 1)) {
			setError(t("portal.inventory.reorder.qtyInvalid"));
			return;
		}
		setBusy(true);
		try {
			const decision = await api.acceptPortalReorderSuggestion(row.id, {
				expectedArrivalDate: date,
				...(override === undefined ? {} : { qtyOverride: override }),
			});
			onDone(
				t("portal.inventory.reorder.accepted", {
					sku: row.skuCode,
					qty: decision.acceptedQty ?? row.suggestedQty,
				}),
			);
		} catch (err) {
			setError(refusal(err, t));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Dialog
			open
			onOpenChange={(open) => !open && onClose()}
			title={t("portal.inventory.reorder.acceptTitle", { sku: row.skuCode })}
			description={t("portal.inventory.reorder.acceptBody", { qty: row.suggestedQty })}
			closeLabel={t("ui.actions.close")}
			footer={
				<>
					<Button variant="secondary" onClick={onClose}>
						{t("ui.actions.cancel")}
					</Button>
					<Button variant="primary" disabled={busy} onClick={() => void submit()}>
						{t("portal.inventory.reorder.accept")}
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-3">
				<Field label={t("portal.inventory.reorder.arrival")} required>
					{(control) => (
						<Input
							{...control}
							type="date"
							value={date}
							onChange={(e) => setDate(e.target.value)}
						/>
					)}
				</Field>
				<Field label={t("portal.inventory.reorder.override")} optional>
					{(control) => (
						<NumberInput
							{...control}
							integerOnly
							placeholder={String(row.suggestedQty)}
							value={qty}
							onChange={(e) => setQty(e.target.value)}
						/>
					)}
				</Field>
				{error !== null && (
					<p role="alert" className="text-[12.5px] text-danger-fg">
						{error}
					</p>
				)}
			</div>
		</Dialog>
	);
}

function DismissDialog({
	row,
	onClose,
	onDone,
}: {
	row: PortalReorderRow;
	onClose: () => void;
	onDone: (message: string) => void;
}) {
	const { t } = useLocale();
	const api = useApi();
	const [reason, setReason] = useState<PortalReorderDismissReason | "">("");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function submit() {
		if (reason === "") {
			setError(t("portal.inventory.reorder.reasonRequired"));
			return;
		}
		setBusy(true);
		try {
			await api.dismissPortalReorderSuggestion(row.id, reason);
			onDone(t("portal.inventory.reorder.dismissed", { sku: row.skuCode }));
		} catch (err) {
			setError(refusal(err, t));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Dialog
			open
			onOpenChange={(open) => !open && onClose()}
			title={t("portal.inventory.reorder.dismissTitle", { sku: row.skuCode })}
			closeLabel={t("ui.actions.close")}
			footer={
				<>
					<Button variant="secondary" onClick={onClose}>
						{t("ui.actions.cancel")}
					</Button>
					<Button variant="danger" disabled={busy} onClick={() => void submit()}>
						{t("portal.inventory.reorder.dismiss")}
					</Button>
				</>
			}
		>
			<Field label={t("portal.inventory.reorder.reason")} error={error ?? undefined}>
				{(control) => (
					<Select
						{...control}
						value={reason}
						onValueChange={(value) => setReason(value as PortalReorderDismissReason)}
						placeholder={t("portal.inventory.reorder.reasonPlaceholder")}
						options={DISMISS_REASONS.map((value) => ({
							value,
							label: t(`portal.inventory.reorder.reasons.${value}`),
						}))}
					/>
				)}
			</Field>
		</Dialog>
	);
}
